import React from 'react';
import { Block } from './Block.jsx';
import { Builder, EditorContainer } from '../layout/Builder.jsx';
import { default as Image } from './Image.jsx';
import Name from './Name.jsx';

function Parallax(props) {
  let className = 'parallax';
  const [activeSlide, setActiveSlide] = React.useState(0);
  let data = props.data;
  if (props.builder) {
    className += ' builder';
    if (data && data.length > 0) {
      if (props.activeSlide != undefined && props.activeSlide < data.length) {
        data = [data[props.activeSlide]];
      } else {
        data = [data[0]];
      }
    }
  }
  let height;
  if (props.data && props.data.length < 2 && props.blockConfig && props.blockConfig.block && props.blockConfig.block.height && props.blockConfig.block.height.value && props.blockConfig.block.height.value.length > 0) {
    height = `${props.blockConfig.block.height.value}${props.blockConfig.block.height.unit}`;
  }
  const backgroundProps = ParallaxUtilities.parseBackgroundProps(data[activeSlide]);
  const fixedBackgroundStyle = backgroundProps.style;
  fixedBackgroundStyle.height = '100vh';
  if (data[activeSlide].image && props.imageSize) {
    fixedBackgroundStyle.backgroundImage = `url('${Image.url(data[activeSlide].image, props.imageSize, props.imageSize)}')`;
  }
  const SlideComponent = props.slideComponent || ParallaxSlide;

  return (
    <div style={{ height }} className={className}>
      <div style={fixedBackgroundStyle} className='parallax__fixed-bkg'></div>
      {data && data.map((d, i) => (
        <ParallaxSlideWrapper
          key={`parallax-${i}`}
          activeSlide={activeSlide}
          d={d}
          height={height || '100vh'}
          idx={i}
          imageSize={props.imageSize}
          setActiveSlide={setActiveSlide}>
          <SlideComponent {...props} data={d} height={height} />
        </ParallaxSlideWrapper>
      ))}
    </div>
  );
}

function ParallaxSlideWrapper(props) {
  const { d, height, children, setActiveSlide, idx, activeSlide } = props;
  const slideRef = React.useRef(undefined);

  React.useEffect(() => {
    const onScroll = e => {
      if (
        slideRef.current.getBoundingClientRect().top < 0 &&
        Math.abs(slideRef.current.getBoundingClientRect().top) < slideRef.current.getBoundingClientRect().height &&
        activeSlide !== idx
      ) {
        setActiveSlide(idx);
      };
    };
    window.addEventListener("scroll", onScroll);

    return () => window.removeEventListener("scroll", onScroll);
  }, [slideRef, activeSlide])

  const data = d || {};
  const backgroundProps = ParallaxUtilities.parseBackgroundProps(data);
  let style;
  if (data.image && idx != activeSlide) {
    style = backgroundProps.style;
    style.backgroundColor = undefined;
    style.height = height;
    if (props.imageSize) {
      style.backgroundImage = `url('${Image.url(data.image, props.imageSize, props.imageSize)}')`;
    }
  } else {
    style = { backgroundColor: 'transparent', backgroundImage: undefined, height };
  }

  return (
    <div ref={slideRef}
      className="parallax__slide-wrapper"
      role="img"
      style={style}
      title={backgroundProps.altText}>
      {children}
    </div>
  )
}

function ParallaxSlide(props) {
  const data = props.data || {};
  const style = { height: props.height };

  let innerClassName = "parallax__content__inner";
  let button;
  if (
    data.button &&
    data.button.text &&
    data.button.text.length > 0 &&
    data.button.link &&
    data.button.link.length > 0
  ) {
    button = (
      <p className="parallax__content__button__cont">
        <a
          className="btn btn--fill parallax__content__button"
          href={data.button.link}
          onClick={(e) => {
            e && e.stopPropagation();
          }}
        >
          {data.button.text}
        </a>
      </p>
    );
    innerClassName += " parallax__content__inner--with__button";
  }
  const content = data.content || {};
  let displayContent = (
    <span className={innerClassName}>
      {content.header && content.header.length > 0 && (
        <p className="parallax__content__header">{content.header}</p>
      )}
      {content.subheader && content.subheader.length > 0 && (
        <p className="parallax__content__subheader">
          {content.subheader}
          <br />
          <br />
        </p>
      )}
    </span>
  );
  if (content.link) {
    displayContent = (
      <a className="parallax__content" href={content.link}>
        {displayContent}
      </a>
    );
  } else {
    displayContent = <div className="parallax__content">{displayContent}</div>;
  }
  if (props.builder) {
    style.pointerEvents = "none";
  }
  return (
    <div className="parallax__slide" style={style}>
      {displayContent}
      {button}
    </div>
  );
}

// #endregion CONTAINER

// #region IMAGE CONTAINER

function ImageParallaxComponent(props) {
  let contentConfig;
  if (props.blockConfig && props.blockConfig.block) {
    contentConfig = props.blockConfig.block.cell;
  }
  return <Parallax {...props} contentConfig={contentConfig} />
}

function ImageParallaxCell(props) {
  const containerStyle = { height: props.height };
  const contentConfig = props.contentConfig || {};
  let blocks = [];
  if (contentConfig.desktop && contentConfig.desktop.blocks) {
    blocks = contentConfig.desktop && contentConfig.desktop.blocks;
  }
  let handleBuilderBlockProps;
  if (props.builder) {
    handleBuilderBlockProps = (d, blockConfig, parentProps) => {
      const blockProps = Builder.handleBlockProps(d, blockConfig, parentProps) || {};
      if (!d.type) {
        return blockProps;
      }
      switch (d.type.value) {
        case Name.Block.Columns:
          if (d.config && d.config.layout && d.config.layout.minHeight && d.config.layout.minHeight.value && d.config.layout.minHeight.value.length > 0) {
            if (!blockProps.style) {
              blockProps.style = {};
            }
            blockProps.style.height = `${d.config.layout.minHeight.value}${d.config.layout.minHeight.unit}`;
          }
          break;
      }
      return blockProps;
    }
    if (!props.editor) {
      containerStyle.pointerEvents = 'none';
    } else if (props.image && props.imageSize) {
      containerStyle.backgroundImage = `url(${Image.url(props.image, props.imageSize, props.imageSize)})`
    }
  }
  const handleContent = (content, style) => <div className="parallax__content" style={style}>{content}</div>
  return (
    <EditorContainer
      {...props}
      blocks={blocks}
      builderBlockPropsHandler={handleBuilderBlockProps}
      className="parallax__slide cell--whitelabel cell--whitelabel--parallax"
      containerProps={contentConfig.desktop && contentConfig.desktop.page}
      inCell={true}
      style={containerStyle}
      updateBackgroundColor={false}
      onContainerProps={props.onPageProps}
      onContent={handleContent} />
  );
}
const ImageContainer = { Cell: ImageParallaxCell, Component: ImageParallaxComponent };

// #endregion IMAGE CONTAINER

const ParallaxUtilities = {
  parseBackgroundProps: (backgroundProps) => {
    const style = { backgroundPosition: 'center center', backgroundSize: 'cover' };
    if (!backgroundProps) {
      return { style };
    }
    let altText, link, newWindow;
    Block.mapStyleConfigProps(style, backgroundProps, (propName, propValue) => {
      switch (propName) {
        case 'altText':
          altText = propValue;
          return true;
        case 'backgroundPosition':
          style.backgroundPosition = Block.backgroundPosition(propValue);
          return true;
        case 'href':
          if (propValue) {
            link = propValue;
          }
          return true;
        case 'newWindow':
          newWindow = propValue;
          return true;
        case 'backgroundRepeatX':
        case 'backgroundRepeatY':
        case 'id':
        case 'image':
        case 'label':
        case 'parallaxContent':
          return true;
      }
    });
    if (style.backgroundSize != 'cover') {
      style.backgroundRepeat = Block.backgroundRepeat(backgroundProps);
    }
    return { altText, link, newWindow, style };
  }
}

export default { Container: { Component: Parallax }, ImageContainer, Utilities: ParallaxUtilities };
